/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useCallback, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { ServiceContext } from "@msx/platform-services";
import {
  postConsumptionAPI,
} from "../../../../../utils/httpUtils";
import {
  SERVICE_NOT_IN_WORKSPACE,
  SERVICE_LOCKED,
  PURL_ERROR_TILE_LOCKED,
  PURL_ERROR_TILE_NOT_FOUND,
} from "../../../../../utils/constants";
import { resolvePdmSupportAssuranceLookupTitle } from "../../../../WebFormPanel/WebFormUtils/pdmSupportAssuranceFeature";

// Module-level URL helpers (exact copy from V2_Tile.tsx module scope)
const urlParamHas = (params: URLSearchParams, name: string): boolean => {
  const lower = name.toLowerCase();
  for (const key of Array.from(params.keys())) {
    if (key.toLowerCase() === lower) return true;
  }
  return false;
};
const urlParamGet = (params: URLSearchParams, name: string): string | null => {
  const lower = name.toLowerCase();
  for (const [key, value] of Array.from(params.entries())) {
    if (key.toLowerCase() === lower) return value;
  }
  return null;
};

// Exact copy from V2_Tile.tsx — utility function
const normalizeString = (str: string | null | undefined): string => {
  return (str || "")
    .normalize("NFKC")
    .replace(/[\u200B-\u200D\uFEFF]/g, "") // Remove zero-width and BOM characters
    .replace(/\u00A0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};
const decodeUrlParam = (value: string): string => {
  try { return decodeURIComponent(value); } catch { return value; }
};

/**
 * usePUrlNavigation — Manages P-URL (Parameterized URL) navigation, auto-pin, and tile resolution.
 *
 * Extracted from V2_Tile.tsx (PR #7, Phase 1).
 * Zero logic changes — pure cut-paste extraction.
 *
 * useRef moved: autoPinAttempted, autoPinInProgress, lastPurlTileName, purlProcessed
 * useState moved: isPUrlSegmentTile, urlTileData
 * Functions moved: validateURL, startTileFlowBasedOnSrc, handleTileNotLoaded,
 *   showPurlTileNotFoundAndRedirect
 * Effects moved: dataServiceWorkspace change handler, PURL guard reset
 */

interface UsePUrlNavigationParams {
  finalTileObject: any;
  setProfileLoading: (val: boolean) => void;
  setShowBot: (val: boolean) => void;
  setIrisUtterance: (val: string) => void;
  setChatBotClicked: (val: boolean) => void;
  setIrisBotIconClicked: (val: boolean) => void;
  setActiveTabKey: (val: string) => void;
  setRefreshHeaderTabCount: (val: boolean) => void;
  refreshHeaderTabCount: boolean;
  isIncidentTile?: boolean;
  // Workspace hook deps
  dataServiceWorkspace: any[];
  setDataServiceWorkspace: (val: any) => void;
  isTileFoundInPreference: boolean;
  setGetTileExecuted: (val: boolean) => void;
  setShowTileNotFoundModal: (val: any) => void;
  updateContextData: (val: any) => void;
  fetchUserWorkspacesAndDefaultTiles: () => Promise<any>;
  validateResponse: (...args: any[]) => any;
  processRedirectServices: (jsonDataWorkspace: any) => Promise<any>;
  // Segment hook deps
  setDialogTitle: (val: string) => void;
  setIrisUtteranceLocal: (val: string) => void;
  setAppName: (val: string) => void;
  setTileName: (val: string) => void;
  setSelectedTileId: (val: any) => void;
  setTileSelected: (val: any) => void;
  setDetailClick: (val: boolean) => void;
  handleStartClick: () => void;
  // UI callbacks from parent
  checkTileSecurity: (tileName: string) => Promise<boolean>;
  addToWorkspace: (id: any, refreshTiles: boolean) => Promise<any>;
  openPanel: (id: any, isRecommended?: boolean, isStartClick?: boolean) => void;
  checkIrisService: (...args: any[]) => Promise<void>;
  setInfoPanelZIndex: (val: string) => void;
  PresetPreference: () => void;
  // PURL guard refs (owned by parent to break circular dep with workspace hook)
  autoPinAttempted: React.MutableRefObject<boolean>;
  autoPinInProgress: React.MutableRefObject<boolean>;
  lastPurlTileName: React.MutableRefObject<string | null>;
  purlProcessed: React.MutableRefObject<boolean>;
  // PURL bridge state (owned by parent, shared with segment hook)
  isPUrlSegmentTile: boolean;
  setIsPUrlSegmentTile: (val: boolean) => void;
  urlTileData: any;
  setUrlTileData: (val: any) => void;
  // Copilot access map for segment P-URL tiles
  copilotAccessMap: Record<string, { hasAccess: boolean; enableTileForm?: boolean; enableSelfHelp?: boolean }>;
  // Shell-level tile name setter (for IRIS welcome message context)
  setTileNameShell?: (val: string) => void;
}

export const usePUrlNavigation = (params: UsePUrlNavigationParams) => {
  const context = React.useContext(ServiceContext);

  // PURL guard refs (owned by parent to break circular dep with workspace hook)
  const { autoPinAttempted, autoPinInProgress, lastPurlTileName, purlProcessed } = params;
  // PURL bridge state (owned by parent, shared with segment hook)
  const { isPUrlSegmentTile, setIsPUrlSegmentTile, urlTileData, setUrlTileData } = params;

  // Track whether copilotAccessMap has been settled (loaded or failed).
  // Prevents P-URL race condition where the flow executes before the map is ready.
  const [copilotMapReady, setCopilotMapReady] = useState(
    Object.keys(params.copilotAccessMap).length > 0
  );

  useEffect(() => {
    if (Object.keys(params.copilotAccessMap).length > 0) {
      setCopilotMapReady(true);
    }
  }, [params.copilotAccessMap]);

  useEffect(() => {
    const handler = () => setCopilotMapReady(true);
    window.addEventListener('copilotAccessMapReady', handler);
    return () => window.removeEventListener('copilotAccessMapReady', handler);
  }, []);

  const validateURL= () => {
    if (params.finalTileObject.isLoaded) return; // Already processed — prevent re-entry on subsequent clicks
    const urlParams = new URLSearchParams(window.location.search);
    const hasTileName = Array.from(urlParams.keys()).some(key => key.toLowerCase() === 'tilename');

    if (hasTileName) {
      params.setIrisUtteranceLocal("");
      params.setTileName("");
      params.setIrisBotIconClicked(true);
    }
  };

  // Helper: show "tile not found" modal and auto-dismiss after 3s with URL cleanup
  const showPurlTileNotFoundAndRedirect = () => {
    params.finalTileObject.isLoaded = true;
    params.finalTileObject.loadForms = false;
    params.setShowTileNotFoundModal({
      isVisible: true,
      message: PURL_ERROR_TILE_NOT_FOUND,
      showCatalogButton: true
    });
    setTimeout(() => {
      window.history.replaceState({}, '', window.location.pathname);
      params.setActiveTabKey('0');
      params.setShowTileNotFoundModal({ isVisible: false, message: "", showCatalogButton: false });
      params.setProfileLoading(false);
    }, 3000);
  };

  const handleTileNotLoaded = () => {
    if (params.finalTileObject.isLoaded === false) {
      if (
        params.finalTileObject.isAmerica === true ||
        params.finalTileObject.isAmericaSMC === true
      ) {
        params.PresetPreference();
      } else {
        params.setProfileLoading(false);
        params.setShowBot(true);
        params.finalTileObject.isLoaded = true;
        params.finalTileObject.loadForms = false;
      }
    }
  };

  const startTileFlowBasedOnSrc = useCallback(
    async (tileNameInSearchQuery) => {
      const normalizedSearch = normalizeString(
        resolvePdmSupportAssuranceLookupTitle(
          decodeUrlParam(String(tileNameInSearchQuery || ""))
        )
      ).toLowerCase();
      // Parse URL params once for the entire flow (avoids 3 redundant parses)
      const urlParams = new URLSearchParams(window.location.search);
      const hasTileName = urlParamHas(urlParams, 'tilename');
      
      // Allow processing for P-URL access even with empty workspace — the auto-pin
      // flow below searches the catalog and pins the tile without needing existing tiles.
      // hasTileName already confirms tilename is present in URL (case-insensitive via urlParamHas),
      // which is sufficient to identify P-URL access. Tilename-only URLs (e.g. ECIF) are also covered.
      const isPUrlAccess = hasTileName;
      if (params.dataServiceWorkspace.length > 0 || isPUrlAccess) {
        const ecifTileName = (process.env.REACT_APP_ECIF_TILE_NAME || "ECIF Support Services").toLowerCase();

        // First try exact match only
        let tile = params.dataServiceWorkspace.find(
          (t) =>
            normalizeString(String(t?.tileName || "")).toLowerCase() === normalizedSearch ||
            normalizeString(String(t?.name || "")).toLowerCase() === normalizedSearch
        );

        // ECIF rename resilience: if searching for old "ECIF Support Services M365" name
        // and not found exactly, fall back to env-var tile name (post-rename target)
        if (!tile && normalizedSearch.includes("ecif")) {
          tile = params.dataServiceWorkspace.find(
            (t) =>
              normalizeString(String(t?.tileName || "")).toLowerCase() === ecifTileName ||
              normalizeString(String(t?.name || "")).toLowerCase() === ecifTileName
          );
        }

        // Only use partial match if no exact/ECIF match found
        if (!tile) {
          const partialMatches = params.dataServiceWorkspace.filter(
            (t) =>
              normalizeString(String(t?.tileName || "")).toLowerCase().includes(normalizedSearch) ||
              normalizeString(String(t?.name || "")).toLowerCase().includes(normalizedSearch)
          );
          // Use partial match ONLY if there's exactly one match (unambiguous)
          if (partialMatches.length === 1) {
            tile = partialMatches[0];
          }
        }
        
        if (tile) {

          // Reuse isPUrlAccess from outer scope — single source of truth
          // for P-URL detection using KNOWN_PURL_KEYS. Avoids drift with hardcoded key lists.

          // P-URL SG-lock check: Block access to SG-locked tiles via PURL (excludes incident tiles)
          // Currently enforced for ECIF tiles; generic check supports future SG-locked tiles via DB config
          if (isPUrlAccess && !params.finalTileObject.isIncidentTile) {
            const isTileLocked = await params.checkTileSecurity(tile.tileName || tile.name);
            if (isTileLocked) {
              params.finalTileObject["Error"] = PURL_ERROR_TILE_LOCKED;
              window.history.replaceState({}, '', window.location.pathname);
              params.setProfileLoading(false);
              params.finalTileObject.isLoaded = true;
              params.finalTileObject.loadForms = false;
              return;
            }
          }

          // Store tile data for P-URL processing
          if (isPUrlAccess) {
            setUrlTileData(tile);
          }

          // CRITICAL: For GSOC tiles (isSegment: true) accessed via P-URL
          if (tile.isSegment && isPUrlAccess) {
            setIsPUrlSegmentTile(true);
            // Block DynamicDropdown auto-process until user explicitly saves segment (prevents ghost ticket)
            sessionStorage.setItem("PUrlSegmentPending", "true");

            // IMPORTANT: Set selectedTileId for rendering the dropdown
            params.setSelectedTileId(tile.serviceID);  // THIS IS CRITICAL
            params.setTileSelected(tile.serviceID);

            // Set segment tile data immediately (no DOM dependency)
            // Override with URL param irisUtterance if provided
            const urlIrisUtteranceSeg = urlParamGet(urlParams, 'irisutterance');
            params.setDialogTitle(tile.name);
            params.setIrisUtteranceLocal(urlIrisUtteranceSeg || tile.iriS_Utterance);
            params.setAppName(tile.irisAppName);
            params.setTileName(tile.tileName);

            // Check if tile has copilot access — only copilot tiles need the extra event dispatch
            const tileKey = (tile.tileName || '').toLowerCase();
            let isCopilotTile = params.copilotAccessMap[tileKey]?.hasAccess === true;
            // Also check redirect target (e.g. AMR Americas → AMR has copilot access)
            if (!isCopilotTile && tile.isRedirect && tile.redirectServiceIdForSMEC) {
              const targetTile = params.dataServiceWorkspace.find((srv) => srv.serviceID === tile.redirectServiceIdForSMEC);
              const targetKey = (targetTile?.tileName || '').toLowerCase();
              isCopilotTile = params.copilotAccessMap[targetKey]?.hasAccess === true;
            }

            if (isCopilotTile) {
              // US-1268661: For copilot segment tiles, open copilot AND Service Info flyout.
              // Segment selection happens later when user clicks "Create an Incident Request" button.
              const mcapsEvent = new CustomEvent('open-mcaps-copilot', {
                detail: { tileName: tile.tileName, serviceID: tile.serviceID, source: 'database', ...(tile.appName && { appName: tile.appName }) }
              });
              window.dispatchEvent(mcapsEvent);
              params.setSelectedTileId(tile.serviceID);
              // Open the Service Info flyout alongside copilot (parity with PROD)
              params.openPanel(tile.serviceID, false, true);
            } else {
              // Non-copilot segment tiles: open panel with segment dropdown
              params.openPanel(tile.serviceID, false, true);
            }

            // Lower z-index after panel renders (for segment popup to appear on top)
            setTimeout(() => params.setInfoPanelZIndex("1000"), 100);

            params.finalTileObject.isLoaded = true;
            params.finalTileObject.loadForms = false;
            params.setProfileLoading(false);
            purlProcessed.current = true;

          } else {
            // PURL flow: Simulate the Start button click on the tile.
            // Exact same sequence as proceedStart() in handleStart:
            // 1. validateURL  2. checkIrisService  3. openPanel(id,false,true)
            // 4. setDetailClick(false)  5. handleStartClick()  6. setTileSelected(id)
            validateURL();
            // Suppress handleChatBotIconClick inside checkIrisService to prevent ghost ticket.
            // The bot is initialized later via unstable_batchedUpdates (setShowBot(true) at line 286).
            sessionStorage.setItem("PUrlBotDeferred", "true");
            await params.checkIrisService(
              tile.name,
              tile.service_IsNonIRISService,
              tile.iriS_Utterance,
              tile.serviceRequestFormLink,
              tile.irisAppName,
              tile.tileName,
              tile.serviceID,
              tile.openinBrowserTab,
              false,
              tile.isSegment,
              tile.appName,
            );
            params.openPanel(tile.serviceID, false, true);
            // Override Iris utterance with URL param if provided (PURL irisUtterance support)
            // Must happen inside batchedUpdates to avoid React 17 race condition:
            // checkIrisService's web form path calls handleChatBotIconClick("", ...) which
            // sets irisUtterance="" and chatBotClicked=true, triggering AppFooter before
            // our override. Batching ensures AppFooter sees the final URL param value.
            const urlIrisUtterance = urlParamGet(urlParams, 'irisutterance');
            sessionStorage.removeItem("PUrlBotDeferred");
            // Check if tile is copilot-enabled — copilot tiles are already handled by
            // ifandelsecondition (open-mcaps-copilot event). Skip IRIS bot initialization
            // to prevent ghost ticket creation behind the copilot panel.
            const tileKeyElse = (tile.tileName || '').toLowerCase();
            let isCopilotTileElse = params.copilotAccessMap[tileKeyElse]?.hasAccess === true;
            if (!isCopilotTileElse && tile.isRedirect && tile.redirectServiceIdForSMEC) {
              const targetTile = params.dataServiceWorkspace.find((srv) => srv.serviceID === tile.redirectServiceIdForSMEC);
              const targetKey = (targetTile?.tileName || '').toLowerCase();
              isCopilotTileElse = params.copilotAccessMap[targetKey]?.hasAccess === true;
            }
            unstable_batchedUpdates(() => {
              params.setDetailClick(false);
              params.handleStartClick();
              params.setTileSelected(tile.serviceID);
              params.setProfileLoading(false);
              if (!isCopilotTileElse) {
                params.setShowBot(true);
                if (urlIrisUtterance) {
                  params.setTileNameShell?.(
                    normalizeString(tile.tileName) || normalizeString(tile.name) || ""
                  );
                  params.setIrisUtterance(urlIrisUtterance);
                  params.setChatBotClicked(true);
                } else if (params.finalTileObject.isIncidentTile) {
                  // ICM-812085024: FAQ-only incident PURLs (Mode=incident, no IrisUtterance).
                  // handleChatBotIconClick is blocked by PUrlBotDeferred — compensate here.
                  // Pass tile's DB utterance as-is (legacy behavior): empty utterance = welcome only,
                  // non-empty = IRIS processes it. No fallback to tile name to avoid auto-FAQ.
                  params.setTileNameShell?.(
                    normalizeString(tile.tileName) || normalizeString(tile.name) || ""
                  );
                  params.setIrisUtterance(tile.iriS_Utterance || "");
                  params.setChatBotClicked(true);
                }
              }
            });
            params.finalTileObject.isLoaded = true;
            params.finalTileObject.loadForms = false;
            purlProcessed.current = true;
          }
          // Handle special cases
          if (
            params.finalTileObject.tileinfo === true &&
            params.finalTileObject.isNonAmerica === true &&
            !tile.isSegment
          ) {
            params.openPanel(tile.serviceID);
          }
          if (
            params.finalTileObject.sendServiceGroup == "true" ||
            params.finalTileObject.sendServiceGroup == true
          ) {
            setTimeout(() => {
              params.finalTileObject.sendServiceGroup = false;
            }, 10000);
          }
          return;
        } else {
          // Tile not found in workspace - check if this is P-URL access (reuse isPUrlAccess from outer scope)
          
          if (isPUrlAccess && !autoPinAttempted.current) {
            // Auto-pin: Search full catalog (v2_services) for the tile and add to workspace
            autoPinAttempted.current = true;
            autoPinInProgress.current = true;
            let catalogTileId = null;
            let tileFoundButLocked = false;
            try {
              // Search v2_services sessionStorage first — contains ALL catalog tiles
              const servicesRaw = sessionStorage.getItem("v2_services");
              if (servicesRaw) {
                const allServices = JSON.parse(servicesRaw);
                if (Array.isArray(allServices)) {
                  // Exact match first (O(1) via Map), fuzzy .includes() only as fallback
                  const exactMap = new Map<string, any>();
                  let fuzzyMatch: any = null;
                  for (const t of allServices) {
                    const normName = normalizeString(String(t?.name || "")).toLowerCase();
                    const normTile = normalizeString(String(t?.tileName || "")).toLowerCase();
                    exactMap.set(normName, t);
                    if (normTile) exactMap.set(normTile, t);
                    if (!fuzzyMatch && (normName.includes(normalizedSearch) || normTile.includes(normalizedSearch))) {
                      fuzzyMatch = t;
                    }
                  }
                  const match = exactMap.get(normalizedSearch) || fuzzyMatch;
                  if (match) {
                    if (match.isSecuredByAzureADGroup) {
                      tileFoundButLocked = true;
                    } else {
                      catalogTileId = match.id;
                    }
                  }
                }
              } else {
                // v2_services not populated yet
              }
              // Fallback: fetch full catalog from GetAllServices API (same as V2_Shell.tsx)
              if (!catalogTileId && !tileFoundButLocked) {
                const catalogResponse = await postConsumptionAPI(
                  `/api/V2_Service/GetAllServices`,
                  { AreaFilter: null, mcemFilter: null, orgSummaryFilter: null },
                  context.authClient
                );
                if (catalogResponse?.data && Array.isArray(catalogResponse.data)) {
                  // Populate v2_services for subsequent access (solves first-load race)
                  sessionStorage.setItem("v2_services", JSON.stringify(catalogResponse.data));
                  let exactApiMatch: any = null;
                  let fuzzyApiMatch: any = null;
                  for (const t of catalogResponse.data) {
                    const normName = normalizeString(String(t?.name || "")).toLowerCase();
                    const normTile = normalizeString(String(t?.tileName || "")).toLowerCase();
                    if (normName === normalizedSearch || normTile === normalizedSearch) {
                      exactApiMatch = t;
                      break;
                    }
                    if (!fuzzyApiMatch && (normName.includes(normalizedSearch) || normTile.includes(normalizedSearch))) {
                      fuzzyApiMatch = t;
                    }
                  }
                  const apiMatch = exactApiMatch || fuzzyApiMatch;
                  if (apiMatch) {
                    if (apiMatch.isSecuredByAzureADGroup) {
                      tileFoundButLocked = true;
                    } else {
                      catalogTileId = apiMatch.id;
                    }
                  }
                }
              }
              // Handle SG-locked tile — show locked error, stop auto-pin
              if (tileFoundButLocked) {
                autoPinInProgress.current = false;
                params.finalTileObject["Error"] = PURL_ERROR_TILE_LOCKED;
                params.setProfileLoading(false);
                params.setShowBot(true);
                params.finalTileObject.isLoaded = true;
                params.finalTileObject.loadForms = false;
                return;
              }
              if (catalogTileId) {
                const addResponse = await params.addToWorkspace(catalogTileId, false);
                if (addResponse.status >= 200 && addResponse.status < 300) {
                  // Fetch fresh workspace data directly from API, bypassing getTiles() cache guard.
                  // getTiles() has a cache guard (tileAlreadyInWorkspace) that returns stale context
                  // data without the newly pinned tile. Calling the API directly ensures we get
                  // the updated workspace including the just-pinned tile.
                  params.finalTileObject.isLoaded = false;
                  const { response1, response2, result1, result2 } = await params.fetchUserWorkspacesAndDefaultTiles();
                  const { isSuccess, jsonDataWorkspace } = params.validateResponse(response1, response2, result1, result2);
                  if (isSuccess) {
                    const redirectResult = await params.processRedirectServices(jsonDataWorkspace);
                    const finalWorkspaceData = redirectResult.isSuccess ? redirectResult.jsonDataWorkspace : jsonDataWorkspace;
                    // CRITICAL: Set autoPinInProgress=false BEFORE setDataServiceWorkspace.
                    // React flushes state updates synchronously inside async functions (after await),
                    // so the useEffect[dataServiceWorkspace] fires IMMEDIATELY when setDataServiceWorkspace
                    // is called — before the next line executes. If autoPinInProgress is still true,
                    // the useEffect skips startTileFlowBasedOnSrc and the post-pin flow never runs.
                    autoPinInProgress.current = false;
                    params.setDataServiceWorkspace(finalWorkspaceData);
                    params.updateContextData(finalWorkspaceData);
                  } else {
                    autoPinInProgress.current = false;
                  }
                  return;
                }
              } else {
                // catalogTileId is null — tile not found in catalog
              }
            } catch (error) {
              console.error("PURL auto-pin failed:", error);
            }
            autoPinInProgress.current = false;
            // Fallback: auto-pin failed or tile not in catalog
            showPurlTileNotFoundAndRedirect();
          } else if (isPUrlAccess && autoPinInProgress.current) {
            // Auto-pin is still in progress (race condition) — wait silently
            return;
          } else if (isPUrlAccess) {
            // Auto-pin completed but tile still not found — show error
            showPurlTileNotFoundAndRedirect();
          } else {
            // Regular tile not found error (existing logic)
            let isTileSecure = await params.checkTileSecurity(tileNameInSearchQuery);
            params.finalTileObject["Error"] = isTileSecure
              ? SERVICE_LOCKED : SERVICE_NOT_IN_WORKSPACE;
            params.setProfileLoading(false);
            params.setShowBot(true);
            params.finalTileObject.isLoaded = true;
            params.finalTileObject.loadForms = false;
          }
        }
      }
    },
    [params.isTileFoundInPreference, params.dataServiceWorkspace, params.copilotAccessMap]
  );

  // Effect: Handle dataServiceWorkspace changes for PURL navigation
  useEffect(() => {
    // Skip re-entry if a catalog-injected tile triggered this effect
    const lastTile = params.dataServiceWorkspace[params.dataServiceWorkspace.length - 1];
    if (lastTile && lastTile._isCatalogInjected) return;

    // For P-URLs: Check if we have a tileName AND it came from URL params.
    const urlParamsEffect = new URLSearchParams(window.location.search);
    const hasPUrlTile = !!params.finalTileObject.TileName && urlParamHas(urlParamsEffect, 'tilename');

    // Race condition guard: wait until copilotAccessMap is settled AND workspace is populated.
    // Without this, the flow fires prematurely and copilot tiles don't open the agent panel.
    if (hasPUrlTile && !purlProcessed.current && (!copilotMapReady || params.dataServiceWorkspace.length === 0)) {
      return;
    }
    
    if (params.isTileFoundInPreference || hasPUrlTile) {
      if (
        (params.dataServiceWorkspace.length > 0 || hasPUrlTile) &&
        params.finalTileObject.isLoaded === false
      ) {
        // Skip startTileFlowBasedOnSrc while auto-pin is in progress — the auto-pin
        // completion handler will call getTiles() and re-trigger this useEffect.
        // CRITICAL: Do NOT fall into handleTileNotLoaded here, as it sets isLoaded=true
        // and permanently blocks the post-pin flow.
        if (!autoPinInProgress.current) {
          startTileFlowBasedOnSrc(params.finalTileObject.TileName);
        }
      } else {
        handleTileNotLoaded();
      }
    }
    sessionStorage.setItem(
      "MyHelp_Tile_Count",
      params.dataServiceWorkspace.length.toString()
    );
    params.setRefreshHeaderTabCount(!params.refreshHeaderTabCount);
  }, [params.dataServiceWorkspace, copilotMapReady]);

  // Reset PURL guards when navigating to a different PURL tile.
  // Without this, autoPinAttempted and isGetTileExecuted block subsequent PURL flows.
  useEffect(() => {
    const currentTileName = params.finalTileObject.TileName || null;
    if (currentTileName && currentTileName !== lastPurlTileName.current) {
      lastPurlTileName.current = currentTileName;
      autoPinAttempted.current = false;
      purlProcessed.current = false;
      params.setGetTileExecuted(false);
    }
  }, [params.finalTileObject.TileName]);

  return {
    autoPinAttempted,
    autoPinInProgress,
    lastPurlTileName,
    purlProcessed,
    isPUrlSegmentTile, setIsPUrlSegmentTile,
    urlTileData, setUrlTileData,
    validateURL,
    startTileFlowBasedOnSrc,
    handleTileNotLoaded,
    showPurlTileNotFoundAndRedirect,
  };
};

// Generated by Copilot
// updated on: 2026-08-18 | 7 lines added | [Auto Generated] | Mapped PDM Support Assurance P-URLs to the preserved Co-Sell database tile record | Time Taken 5 min
